import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Persisted read/dismiss state for the notification center.
 *
 * Notifications are *derived* from live REST data (pending approvals, upcoming
 * appointments), so their IDs are stable and belong to the source records.
 * We only need to remember which ones the user has already read or dismissed.
 */
interface NotificationReadState {
  readIds: Record<string, true>
  dismissedIds: Record<string, true>

  isRead: (id: string) => boolean
  isDismissed: (id: string) => boolean
  markRead: (id: string) => void
  markManyRead: (ids: string[]) => void
  dismiss: (id: string) => void
}

export const useNotificationReadStore = create<NotificationReadState>()(
  persist(
    (set, get) => ({
      readIds: {},
      dismissedIds: {},

      isRead: (id) => Boolean(get().readIds[id]),
      isDismissed: (id) => Boolean(get().dismissedIds[id]),

      markRead: (id) =>
        set((state) => ({ readIds: { ...state.readIds, [id]: true } })),

      markManyRead: (ids) =>
        set((state) => {
          const readIds = { ...state.readIds }
          for (const id of ids) readIds[id] = true
          return { readIds }
        }),

      dismiss: (id) =>
        set((state) => ({
          dismissedIds: { ...state.dismissedIds, [id]: true },
          readIds: { ...state.readIds, [id]: true },
        })),
    }),
    {
      name: 'hms-notification-read',
      partialize: (state) => ({
        readIds: state.readIds,
        dismissedIds: state.dismissedIds,
      }),
    }
  )
)
